"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "../../Button";
import { Dialog } from "../../Dialog";

interface ExpandedSize {
  width: number;
  height: number;
}

export interface ChartExpand {
  isExpanded: boolean;
  /** The expand control, for `ChartHeader`'s action slot. */
  action: ReactNode;
  /**
   * Dimensions to render at. `null` inline, where the `withParentSize` HOC's
   * own measurements apply.
   */
  expandedSize: ExpandedSize | null;
  /** Wraps the chart body: inline it passes through, expanded it opens a Dialog. */
  present: (body: ReactNode) => ReactNode;
}

/**
 * Universal expand for both chart engines, rendered through `ChartHeader`.
 *
 * ## Why the engine keeps its own state
 *
 * The obvious build is a second chart inside a Dialog, which is what the two
 * hand-rolled versions this replaces did. It costs you every piece of chart
 * state: the Dialog is an overlay portal, so the copy inside it is a different
 * component instance starting from defaults. `SiteEnergyCharts` worked around
 * that by lifting `hiddenSeriesLabels` and `zoomDomain` into its parent and
 * feeding both instances — correct, but it has to be redone for every stateful
 * feature anyone adds, and forgetting once turns expand into a reset button.
 *
 * Here only the *body* moves. The engine component is never unmounted, so its
 * state — zoom domain, hidden series, tiled camera, and anything added later
 * without reading this comment — survives because nothing ever tears it down.
 *
 * ## Why it measures again
 *
 * `withParentSize` observes a container it renders itself, above the body. That
 * container stays behind in the layout when the body moves, so it keeps
 * reporting the inline size — and doubles as a placeholder that stops the page
 * reflowing. Expanded, the body is measured where it actually is, and the
 * engine renders to `expandedSize` instead.
 */
/**
 * Does an ancestor already own the expand affordance for this chart?
 *
 * Some charts sit inside a panel or page section that must expand as a *unit* —
 * the telemetry panels expand a chart, a SCADA SVG or a table depending on the
 * view mode, and Site Energy expands its chart together with its time controls
 * and KPI strip. Those containers own expand and call this hook themselves; the
 * chart inside would otherwise render a second, narrower expand button beside
 * the real one.
 *
 * Same shape as `ChartLabelledBy` for titles: the container declares itself
 * once, rather than every chart carrying an opt-out prop someone must remember.
 */
const ChartExpandOwnedContext = createContext(false);

/** Marks a subtree whose expand is owned by an ancestor. */
export function ChartExpandOwnedByAncestor({ children }: { children: ReactNode }) {
  return <ChartExpandOwnedContext.Provider value={true}>{children}</ChartExpandOwnedContext.Provider>;
}

export interface ChartExpandOptions {
  /**
   * Keep the body mounted across expand. Without it the body changes tree position (inline, then inside the
   * Dialog), so React remounts it: fine for a chart whose state lives above `present`, not for a host that wraps
   * whole blocks — they lose their resolved data and their state. With it the body renders once, into a node
   * this hook moves between the inline slot and the Dialog; the inline slot keeps its height so the page does
   * not jump.
   */
  keepMounted?: boolean;
}

/** A host that stops presenting the body (its section removed) must not reopen the dialog when the body returns. */
function CloseOnUnmount({ close }: { close: () => void }) {
  useEffect(() => close, [close]);
  return null;
}

export function useChartExpand(title: string, options?: ChartExpandOptions): ChartExpand {
  const ownedByAncestor = useContext(ChartExpandOwnedContext);
  const keepMounted = options?.keepMounted === true;
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSize, setExpandedSize] = useState<ExpandedSize | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const measureRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setExpandedSize((prev) => (prev && prev.width === width && prev.height === height ? prev : { width, height }));
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  // `keepMounted`: the node the body portals into, and the two places it can sit.
  const [portalNode, setPortalNode] = useState<HTMLDivElement | null>(null);
  const [inlineHeight, setInlineHeight] = useState<number | null>(null);
  const inlineSlotRef = useRef<HTMLDivElement | null>(null);
  // A layout effect, so the body's first render already goes through the portal: no in-place mount to tear down.
  useLayoutEffect(() => {
    if (!keepMounted) return;
    const node = document.createElement("div");
    node.style.height = "100%";
    setPortalNode(node);
    return () => node.remove();
  }, [keepMounted]);
  const inlineSlot = useCallback(
    (slot: HTMLDivElement | null) => {
      inlineSlotRef.current = slot;
      if (slot && portalNode && !isExpanded) slot.appendChild(portalNode);
    },
    [portalNode, isExpanded]
  );
  const dialogSlot = useCallback(
    (slot: HTMLDivElement | null) => {
      if (slot && portalNode) slot.appendChild(portalNode);
    },
    [portalNode]
  );

  const close = useCallback(() => {
    setIsExpanded(false);
    setInlineHeight(null);
    // Drop the stale measurement so a re-open cannot paint one frame at the
    // previous dialog's size before the observer reports.
    setExpandedSize(null);
  }, []);

  const action = ownedByAncestor ? null : (
    <Button
      variant="icon"
      size="sm"
      icon="ArrowsOutSimple"
      aria-label={`Expand ${title} chart`}
      onPress={() => {
        setInlineHeight(inlineSlotRef.current?.offsetHeight ?? null);
        setIsExpanded(true);
      }}
    />
  );

  const dialog = useCallback(
    (content: ReactNode) => (
      <Dialog
        isOpen
        onClose={close}
        title={title}
        contentPadding={false}
        maxWidth="min(1200px, 100vw)"
        minWidth="min(360px, 100vw)"
        height="82vh"
        mobileHeight="82vh"
      >
        <div ref={measureRef} className="h-full w-full p-4">
          {content}
        </div>
      </Dialog>
    ),
    [close, title, measureRef]
  );

  const present = useCallback(
    (body: ReactNode) => {
      const expanded = isExpanded && !ownedByAncestor;
      if (!keepMounted) return expanded ? dialog(body) : body;
      return (
        <>
          <CloseOnUnmount close={close} />
          {portalNode ? createPortal(body, portalNode) : null}
          <div ref={inlineSlot} style={expanded && inlineHeight !== null ? { height: inlineHeight } : undefined} />
          {expanded ? dialog(<div ref={dialogSlot} className="h-full w-full" />) : null}
        </>
      );
    },
    [isExpanded, ownedByAncestor, dialog, close, keepMounted, portalNode, inlineSlot, dialogSlot, inlineHeight]
  );

  return { isExpanded, action, expandedSize, present };
}
